import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { ScrollView, View } from 'react-native';
import {
  Badge, Button, Chip, DataTable, Dialog, Divider, IconButton, List, Portal,
  SegmentedButtons, Text, TextInput, useTheme,
} from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { useMutation } from '@apollo/client';
import { Popover } from 'native-base';
import SelectBox from 'react-native-multi-selectbox';
import { useToast } from 'react-native-toast-notifications';
import { AGE_CATEGORY, ROLE_NAMES, SCREEN_NAMES } from '../../../utils/constant';
import {
  DeleteUserMutation,
  UPDATE_USER_ROLE,
  CONVERT_TO_CHILD_ACCOUNT,
  RESEND_CHILD_INVITE,
  UNLINK_CHILD_ACCOUNT,
  ValidateContribution,
  RevokeContribution,
  ValidateSecurityDeposit,
  RevokeSecurityDeposit,
} from '../../../gql/user/mutation';
import { AuthContext } from '../../../context/AuthContext';
import { ModalContext } from '../../../context/ModalContext';
import ConfirmationModal from '../../../modals/Confirmation/ConfirmationModal';
import { computeCategorie, computeWeightBasedOnCategorie } from '../../../utils/utils';
import LoadingIndicator from '../../../components/commons/LoadingIndicator';
import UserAvatarBadge from '../../../components/commons/UserAvatarBadge';
import useDesktopLayout from '../../../hooks/useDesktopLayout';

const USER_STATUSES = [
  { id: 'COMPETITOR', name: 'Compétiteur' },
  { id: 'REVIEW_PENDING', name: 'En attente' },
  { id: 'ACTIVE', name: 'Actif' },
  { id: 'INACTIVE', name: 'Inactif' },
];

function CompetitorsTab({
  users, season, searchQuery, loading, refetch,
}) {
  const navigation = useNavigation();
  const theme = useTheme();
  const toast = useToast();
  const { authState } = useContext(AuthContext);
  const { displayConfirmationModal, toggleConfirmationModal } = useContext(ModalContext);
  const isSuperAdmin = authState.role === 'SUPERADMIN';
  const isDesktop = useDesktopLayout();

  const [filtersVisible, setFiltersVisible] = useState(false);
  const [groupingType, setGroupingType] = useState('national');
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [contributionFilter, setContributionFilter] = useState('all');
  const [appliedGroupingType, setAppliedGroupingType] = useState('national');
  const [appliedFilterCategories, setAppliedFilterCategories] = useState([]);
  const [appliedFilterStatuses, setAppliedFilterStatuses] = useState([]);
  const [appliedContributionFilter, setAppliedContributionFilter] = useState('all');
  const [sortedUsers, setSortedUsers] = useState({});
  const [filteredUsers, setFilteredUsers] = useState({});

  const competitorUsers = useMemo(
    () => users.filter((u) => u.role === ROLE_NAMES.USER && u.status !== 'COMPANION'),
    [users],
  );

  const [deleteOneUser, { error: deleteError }] = useMutation(DeleteUserMutation, {
    onCompleted: (_data) => {
      toast.show(_data.deleteOneUser.message);
      refetch();
    },
  });

  const [updateUserRole] = useMutation(UPDATE_USER_ROLE, {
    onCompleted: (_data) => {
      toast.show(_data.updateUserRole.message);
      refetch();
    },
  });

  const [convertToChild] = useMutation(CONVERT_TO_CHILD_ACCOUNT, {
    onCompleted: (_data) => { toast.show(_data.convertToChildAccount.message); refetch(); },
    onError: (e) => toast.show(e.message),
  });

  const [resendChildInvite] = useMutation(RESEND_CHILD_INVITE, {
    onCompleted: (_data) => { toast.show(_data.resendChildInvite.message); refetch(); },
    onError: (e) => toast.show(e.message),
  });

  const [unlinkChild] = useMutation(UNLINK_CHILD_ACCOUNT, {
    onCompleted: (_data) => { toast.show(_data.unlinkChildAccount.message); refetch(); },
    onError: (e) => toast.show(e.message),
  });

  const [validateContribution] = useMutation(ValidateContribution, {
    onCompleted: () => { toast.show('Cotisation validée'); refetch(); },
    onError: (e) => toast.show(e.message),
  });

  const [revokeContribution] = useMutation(RevokeContribution, {
    onCompleted: (_data) => { toast.show(_data.revokeContribution.message); refetch(); },
    onError: (e) => toast.show(e.message),
  });

  const [validateSecurityDeposit] = useMutation(ValidateSecurityDeposit, {
    onCompleted: () => { toast.show('Caution validée'); refetch(); },
    onError: (e) => toast.show(e.message),
  });

  const [revokeSecurityDeposit] = useMutation(RevokeSecurityDeposit, {
    onCompleted: (_data) => { toast.show(_data.revokeSecurityDeposit.message); refetch(); },
    onError: (e) => toast.show(e.message),
  });

  const [convertDialog, setConvertDialog] = useState({ visible: false, userId: null, parentEmail: '' });

  if (deleteError) toast.show(deleteError.message);

  useEffect(() => {
    if (competitorUsers.length > 0 && season) {
      const withCategories = competitorUsers.map((user) => ({
        ...user,
        categories: user.profile
          ? computeCategorie(new Date(user.profile.dateOfBirth), season)
          : { national: ['Inconnu'], international: ['Inconnu'] },
      }));

      const groupKey = appliedGroupingType === 'national' ? 'national' : 'international';
      const grouped = {};
      withCategories.forEach((user) => {
        user.categories[groupKey].forEach((category) => {
          if (!grouped[category]) grouped[category] = [];
          grouped[category].push(user);
        });
      });

      const categoryOrder = Object.values(AGE_CATEGORY).map((c) => c.name);
      const sorted = Object.keys(grouped)
        .sort((a, b) => {
          if (a === 'Inconnu') return 1;
          if (b === 'Inconnu') return -1;
          return categoryOrder.indexOf(a) - categoryOrder.indexOf(b);
        })
        .reduce((obj, key) => { obj[key] = grouped[key]; return obj; }, {});

      setSortedUsers(sorted);
      setFilteredUsers(sorted);
    } else {
      setSortedUsers({});
      setFilteredUsers({});
    }
  }, [competitorUsers, season, appliedGroupingType]);

  useEffect(() => {
    let result = sortedUsers;
    if (appliedFilterCategories.length > 0) {
      result = Object.keys(result)
        .filter((k) => appliedFilterCategories.includes(k))
        .reduce((obj, k) => { obj[k] = result[k]; return obj; }, {});
    }
    if (appliedFilterStatuses.length > 0) {
      const filtered = {};
      Object.keys(result).forEach((category) => {
        const list = result[category].filter((u) => appliedFilterStatuses.includes(u.status));
        if (list.length > 0) filtered[category] = list;
      });
      result = filtered;
    }
    if (appliedContributionFilter !== 'all') {
      const filtered = {};
      Object.keys(result).forEach((category) => {
        const list = result[category].filter((u) => {
          const hasPaid = season
            && u.contributions?.some((c) => c.seasonId === Number(season.id));
          return appliedContributionFilter === 'paid' ? hasPaid : !hasPaid;
        });
        if (list.length > 0) filtered[category] = list;
      });
      result = filtered;
    }
    setFilteredUsers(result);
  }, [
    appliedFilterCategories, appliedFilterStatuses, appliedContributionFilter, sortedUsers, season,
  ]);

  const applyFilters = () => {
    setAppliedGroupingType(groupingType);
    setAppliedFilterCategories(filterCategories);
    setAppliedFilterStatuses(filterStatuses);
    setAppliedContributionFilter(contributionFilter);
    setFiltersVisible(false);
  };

  const clearFilters = () => {
    setGroupingType('national');
    setFilterCategories([]);
    setFilterStatuses([]);
    setContributionFilter('all');
  };

  const activeFilterCount = (appliedGroupingType !== 'national' ? 1 : 0)
    + appliedFilterCategories.length
    + appliedFilterStatuses.length
    + (appliedContributionFilter !== 'all' ? 1 : 0);

  const userStatusOptions = useMemo(
    () => USER_STATUSES.map((s) => ({ id: s.id, item: s.name })),
    [],
  );

  const filterControls = (
    <>
      <View style={{ paddingBottom: 8 }}>
        <SegmentedButtons
          value={groupingType}
          onValueChange={(v) => v && setGroupingType(v)}
          buttons={[
            { value: 'national', label: 'National' },
            { value: 'international', label: 'International' },
          ]}
        />
      </View>
      <Divider style={{ marginVertical: 8 }} />
      <SelectBox
        label={filterCategories.length > 0 ? `${filterCategories.length} cat. sélectionnée(s)` : 'Filtrer par catégories'}
        options={Object.keys(sortedUsers).map((c) => ({ id: c, item: c }))}
        selectedValues={[]}
        onMultiSelect={(item) => setFilterCategories((prev) => (prev.includes(item.id)
          ? prev.filter((c) => c !== item.id) : [...prev, item.id]))}
        onTapClose={(item) => setFilterCategories((prev) => prev.filter((c) => c !== item.id))}
        isMulti
        arrowIconColor={theme.colors.primary}
        searchIconColor={theme.colors.primary}
        toggleIconColor={theme.colors.primary}
        multiOptionContainerStyle={{ backgroundColor: theme.colors.primary }}
        optionsLabelStyle={{ color: theme.colors.text }}
        containerStyle={{ borderBottomWidth: 0 }}
      />
      <View style={{
        flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingTop: 8,
      }}
      >
        {filterCategories.map((c) => (
          <Chip key={c} icon="close" onPress={() => setFilterCategories((prev) => prev.filter((x) => x !== c))}>
            {c}
          </Chip>
        ))}
      </View>
      <Divider style={{ marginVertical: 8 }} />
      <SelectBox
        label={filterStatuses.length > 0 ? `${filterStatuses.length} statut(s) sélectionné(s)` : 'Filtrer par statut'}
        options={userStatusOptions}
        selectedValues={[]}
        onMultiSelect={(item) => setFilterStatuses((prev) => (prev.includes(item.id)
          ? prev.filter((s) => s !== item.id) : [...prev, item.id]))}
        onTapClose={(item) => setFilterStatuses((prev) => prev.filter((s) => s !== item.id))}
        isMulti
        arrowIconColor={theme.colors.primary}
        searchIconColor={theme.colors.primary}
        toggleIconColor={theme.colors.primary}
        multiOptionContainerStyle={{ backgroundColor: theme.colors.primary }}
        optionsLabelStyle={{ color: theme.colors.text }}
        containerStyle={{ borderBottomWidth: 0 }}
      />
      <View style={{
        flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingTop: 8,
      }}
      >
        {filterStatuses.map((statusId) => (
          <Chip key={statusId} icon="close" onPress={() => setFilterStatuses((prev) => prev.filter((s) => s !== statusId))}>
            {USER_STATUSES.find((s) => s.id === statusId)?.name}
          </Chip>
        ))}
      </View>
      <Divider style={{ marginVertical: 8 }} />
      <Text style={{ marginBottom: 4 }}>Cotisation</Text>
      <SegmentedButtons
        value={contributionFilter}
        onValueChange={(v) => v && setContributionFilter(v)}
        buttons={[
          { value: 'all', label: 'Toutes' },
          { value: 'paid', label: 'Payée' },
          { value: 'unpaid', label: 'Non payée' },
        ]}
      />
    </>
  );

  return (
    <>
      {displayConfirmationModal.display && <ConfirmationModal />}

      <Portal>
        <Dialog visible={convertDialog.visible} onDismiss={() => setConvertDialog({ visible: false, userId: null, parentEmail: '' })}>
          <Dialog.Title>Lier à un parent</Dialog.Title>
          <Dialog.Content>
            <Text style={{ marginBottom: 8 }}>Email du parent / responsable légal :</Text>
            <TextInput
              mode="outlined"
              value={convertDialog.parentEmail}
              onChangeText={(v) => setConvertDialog((prev) => ({ ...prev, parentEmail: v }))}
              keyboardType="email-address"
              autoCapitalize="none"
              label="Email parent"
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setConvertDialog({ visible: false, userId: null, parentEmail: '' })}>Annuler</Button>
            <Button
              onPress={() => {
                convertToChild({ variables: { userId: convertDialog.userId, parentEmail: convertDialog.parentEmail } });
                setConvertDialog({ visible: false, userId: null, parentEmail: '' });
              }}
            >
              Envoyer l'invitation
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <View style={{ flex: 1 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', paddingHorizontal: 4 }}>
          <IconButton
            icon="account-plus"
            onPress={() => navigation.navigate(SCREEN_NAMES.INVITE)}
          />
          <Popover
            isOpen={filtersVisible}
            onClose={() => setFiltersVisible(false)}
            trigger={(triggerProps) => (
              <View {...triggerProps}>
                <IconButton icon="filter-variant" onPress={() => setFiltersVisible(!filtersVisible)} />
                {activeFilterCount > 0 && (
                  <Badge style={{ position: 'absolute', top: 5, right: 5 }}>{activeFilterCount}</Badge>
                )}
              </View>
            )}
          >
            <Popover.Content accessibilityLabel="Filtres" w={isDesktop ? '96' : '80'}>
              <Popover.Arrow />
              <Popover.CloseButton />
              <Popover.Header>Filtrer les compétiteurs</Popover.Header>
              <Popover.Body>
                {filterControls}
              </Popover.Body>
              <Popover.Footer justifyContent="space-between">
                <Button onPress={clearFilters}>Effacer</Button>
                <Button mode="contained" onPress={applyFilters}>Appliquer</Button>
              </Popover.Footer>
            </Popover.Content>
          </Popover>
        </View>
        {loading && <LoadingIndicator text="Chargement des utilisateurs..." />}
        <ScrollView style={{ flex: 1 }}>
          {Object.keys(filteredUsers).map((category) => {
            const matching = filteredUsers[category]?.filter(
              (u) => u.firstName?.toLowerCase().includes(searchQuery)
                || u.lastName?.toLowerCase().includes(searchQuery),
            );
            if (!matching || matching.length === 0) return null;
            return (
              <List.Section key={category}>
                <List.Subheader>
                  {`${category} - ${matching.length} ${matching.length <= 1 ? 'compétiteur' : 'compétiteurs'}`}
                </List.Subheader>
                {(() => {
                  const rows = matching.map((d) => {
                    const weight = d.categories.national.length > 0 && d.profile?.weights.length > 0
                      ? computeWeightBasedOnCategorie(
                        d.profile.weights[d.profile.weights.length - 1].value,
                        d.profile.gender,
                        d.categories.national[0],
                      ) : undefined;
                    const icon = d.status === 'COMPETITOR' ? 'karate'
                      : d.status === 'ACTIVE' ? 'account'
                        : d.status === 'INACTIVE' ? 'progress-clock'
                          : 'account-reactivate';
                    const hasPaidContribution = season
                      && d.contributions?.some((c) => c.seasonId === Number(season.id));
                    const toggleContribution = () => {
                      const variables = { userId: d.id, seasonId: Number(season.id) };
                      if (hasPaidContribution) revokeContribution({ variables });
                      else validateContribution({ variables });
                    };
                    const hasPaidSecurityDeposit = season
                      && d.securityDeposits?.some((c) => c.seasonId === Number(season.id));
                    const toggleSecurityDeposit = () => {
                      const variables = { userId: d.id, seasonId: Number(season.id) };
                      if (hasPaidSecurityDeposit) revokeSecurityDeposit({ variables });
                      else validateSecurityDeposit({ variables });
                    };

                    if (isDesktop) {
                      return (
                        <DataTable.Row key={d.id}>
                          <DataTable.Cell>{`${d.firstName} ${d.lastName}`}</DataTable.Cell>
                          <DataTable.Cell>{d.email}</DataTable.Cell>
                          <DataTable.Cell>{d.profile?.license ?? 'Aucune licence'}</DataTable.Cell>
                          <DataTable.Cell>
                            {USER_STATUSES.find((s) => s.id === d.status)?.name ?? d.status}
                          </DataTable.Cell>
                          <DataTable.Cell>{weight ? `${weight}kg` : '-'}</DataTable.Cell>
                          <DataTable.Cell>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                              <Text style={{ color: hasPaidContribution ? '#2e7d32' : '#c62828' }}>
                                {hasPaidContribution ? 'Payée' : 'Non payée'}
                              </Text>
                              {isSuperAdmin && season && (
                                <IconButton
                                  icon={hasPaidContribution ? 'cash-remove' : 'cash-check'}
                                  size={16}
                                  onPress={toggleContribution}
                                />
                              )}
                            </View>
                          </DataTable.Cell>
                          <DataTable.Cell>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                              <Text style={{ color: hasPaidSecurityDeposit ? '#2e7d32' : '#c62828' }}>
                                {hasPaidSecurityDeposit ? 'Payée' : 'Non payée'}
                              </Text>
                              {isSuperAdmin && season && (
                                <IconButton
                                  icon={hasPaidSecurityDeposit ? 'shield-remove' : 'shield-check'}
                                  size={16}
                                  onPress={toggleSecurityDeposit}
                                />
                              )}
                            </View>
                          </DataTable.Cell>
                          <DataTable.Cell>
                            {d.parentId && (
                              <Chip icon="account-child" compact>
                                {d.parent?.firstName ?? 'Parent lié'}
                              </Chip>
                            )}
                            {d.childInvite && !d.parentId && (
                              <Chip icon="clock-outline" compact style={{ backgroundColor: '#fff3cd' }}>
                                Invite en attente
                              </Chip>
                            )}
                          </DataTable.Cell>
                          <DataTable.Cell numeric>
                            <View style={{ flexDirection: 'row' }}>
                              {isSuperAdmin ? (
                                <Popover
                                  trigger={(triggerProps) => (
                                    <UserAvatarBadge
                                      user={d}
                                      icon={icon}
                                      triggerProps={triggerProps}
                                    />
                                  )}
                                >
                                  <Popover.Content>
                                    <Popover.Body>
                                      <Button contentStyle={{ justifyContent: 'flex-start' }} onPress={() => updateUserRole({ variables: { id: d.id, role: ROLE_NAMES.USER } })}>
                                        Utilisateur
                                      </Button>
                                      <Button contentStyle={{ justifyContent: 'flex-start' }} onPress={() => updateUserRole({ variables: { id: d.id, role: ROLE_NAMES.COACH } })}>
                                        Coach
                                      </Button>
                                    </Popover.Body>
                                  </Popover.Content>
                                </Popover>
                              ) : (
                                <UserAvatarBadge user={d} icon={icon} />
                              )}
                              {d.status !== 'INACTIVE' && (
                                <IconButton icon="eye" onPress={() => navigation.navigate({ name: SCREEN_NAMES.PROFILE, params: { email: d.email } })} />
                              )}
                              {isSuperAdmin && (
                                <>
                                  {!d.parentId && !d.childInvite && (
                                    <IconButton
                                      icon="account-child-outline"
                                      onPress={() => setConvertDialog({ visible: true, userId: d.id, parentEmail: '' })}
                                    />
                                  )}
                                  {d.childInvite && !d.parentId && (
                                    <IconButton
                                      icon="email-sync-outline"
                                      onPress={() => resendChildInvite({
                                        variables: { childId: d.id },
                                      })}
                                    />
                                  )}
                                  {d.parentId && (
                                    <IconButton
                                      icon="link-variant-off"
                                      onPress={() => toggleConfirmationModal({
                                        title: 'Délier le compte',
                                        message: `Voulez-vous délier ${d.firstName} ${d.lastName} de son compte parent ?`,
                                        handleSubmit: async () => {
                                          await unlinkChild({ variables: { childId: d.id } });
                                        },
                                      })}
                                    />
                                  )}
                                  <IconButton
                                    icon="delete"
                                    onPress={() => toggleConfirmationModal({
                                      title: 'Confirmation',
                                      message: "Voulez-vous vraiment supprimer définitivement l'utilisateur ? Cette action est irréversible.",
                                      handleSubmit: async () => {
                                        await deleteOneUser({ variables: { email: d.email } });
                                      },
                                    })}
                                  />
                                </>
                              )}
                            </View>
                          </DataTable.Cell>
                        </DataTable.Row>
                      );
                    }

                    return (
                    <List.Item
                      key={d.id}
                      title={`${d.firstName} ${d.lastName}`}
                      description={(props) => (
                        <View>
                          <Text {...props}>{d.email}</Text>
                          <Text {...props} style={{ fontWeight: 'bold' }}>{d.profile?.license ?? 'Aucune licence'}</Text>
                          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Text {...props} style={{ color: hasPaidContribution ? '#2e7d32' : '#c62828' }}>
                              {hasPaidContribution ? 'Cotisation payée' : 'Cotisation non payée'}
                            </Text>
                            {isSuperAdmin && season && (
                              <IconButton
                                icon={hasPaidContribution ? 'cash-remove' : 'cash-check'}
                                size={16}
                                onPress={toggleContribution}
                              />
                            )}
                          </View>
                          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Text {...props} style={{ color: hasPaidSecurityDeposit ? '#2e7d32' : '#c62828' }}>
                              {hasPaidSecurityDeposit ? 'Caution équipement payée' : 'Caution équipement non payée'}
                            </Text>
                            {isSuperAdmin && season && (
                              <IconButton
                                icon={hasPaidSecurityDeposit ? 'shield-remove' : 'shield-check'}
                                size={16}
                                onPress={toggleSecurityDeposit}
                              />
                            )}
                          </View>
                        </View>
                      )}
                      left={(props) => {
                        if (isSuperAdmin) {
                          return (
                            <Popover
                              trigger={(triggerProps) => (
                                <UserAvatarBadge
                                  user={d}
                                  icon={icon}
                                  style={props.style}
                                  triggerProps={triggerProps}
                                />
                              )}
                            >
                              <Popover.Content>
                                <Popover.Body>
                                  <Button contentStyle={{ justifyContent: 'flex-start' }} onPress={() => updateUserRole({ variables: { id: d.id, role: ROLE_NAMES.USER } })}>
                                    Utilisateur
                                  </Button>
                                  <Button contentStyle={{ justifyContent: 'flex-start' }} onPress={() => updateUserRole({ variables: { id: d.id, role: ROLE_NAMES.COACH } })}>
                                    Coach
                                  </Button>
                                </Popover.Body>
                              </Popover.Content>
                            </Popover>
                          );
                        }
                        return <UserAvatarBadge user={d} icon={icon} style={props.style} />;
                      }}
                      right={(props) => (
                        <>
                          <View style={{ justifyContent: 'center', gap: 4 }}>
                            {weight && (
                            <Chip {...props}>
                              {weight}
                              kg
                            </Chip>
                            )}
                            {d.parentId && (
                              <Chip {...props} icon="account-child" compact>
                                {d.parent?.firstName ?? 'Parent lié'}
                              </Chip>
                            )}
                            {d.childInvite && !d.parentId && (
                              <Chip {...props} icon="clock-outline" compact style={{ backgroundColor: '#fff3cd' }}>
                                Invite en attente
                              </Chip>
                            )}
                          </View>
                          {d.status !== 'INACTIVE' && (
                            <IconButton {...props} icon="eye" onPress={() => navigation.navigate({ name: SCREEN_NAMES.PROFILE, params: { email: d.email } })} />
                          )}
                          {isSuperAdmin && (
                            <>
                              {!d.parentId && !d.childInvite && (
                                <IconButton
                                  {...props}
                                  icon="account-child-outline"
                                  onPress={() => setConvertDialog({ visible: true, userId: d.id, parentEmail: '' })}
                                />
                              )}
                              {d.childInvite && !d.parentId && (
                                <IconButton
                                  {...props}
                                  icon="email-sync-outline"
                                  onPress={() => resendChildInvite({ variables: { childId: d.id } })}
                                />
                              )}
                              {d.parentId && (
                                <IconButton
                                  {...props}
                                  icon="link-variant-off"
                                  onPress={() => toggleConfirmationModal({
                                    title: 'Délier le compte',
                                    message: `Voulez-vous délier ${d.firstName} ${d.lastName} de son compte parent ?`,
                                    handleSubmit: async () => { await unlinkChild({ variables: { childId: d.id } }); },
                                  })}
                                />
                              )}
                              <IconButton
                                {...props}
                                icon="delete"
                                onPress={() => toggleConfirmationModal({
                                  title: 'Confirmation',
                                  message: "Voulez-vous vraiment supprimer définitivement l'utilisateur ? Cette action est irréversible.",
                                  handleSubmit: async () => { await deleteOneUser({ variables: { email: d.email } }); },
                                })}
                              />
                            </>
                          )}
                        </>
                      )}
                    />
                    );
                  });

                  if (isDesktop) {
                    return (
                      <DataTable>
                        <DataTable.Header>
                          <DataTable.Title>Nom</DataTable.Title>
                          <DataTable.Title>Email</DataTable.Title>
                          <DataTable.Title>Licence</DataTable.Title>
                          <DataTable.Title>Statut</DataTable.Title>
                          <DataTable.Title>Poids</DataTable.Title>
                          <DataTable.Title>Cotisation</DataTable.Title>
                          <DataTable.Title>Caution</DataTable.Title>
                          <DataTable.Title>Parent/Invite</DataTable.Title>
                          <DataTable.Title numeric>Actions</DataTable.Title>
                        </DataTable.Header>
                        {rows}
                      </DataTable>
                    );
                  }
                  return rows;
                })()}
              </List.Section>
            );
          })}
        </ScrollView>
      </View>
    </>
  );
}

export default CompetitorsTab;
